import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePortalAuth } from '../../hooks/usePortalAuth';
import * as portalLanguageService from '../../services/portalLanguageService';
import { usePortalAuthStore } from '../../stores/portalAuthStore';
import { CountryFlag } from './CountryFlag';

interface ActiveLanguage {
	id: number;
	code: string;
	name: string;
}

/**
 * Header language switcher — compact button with globe icon + 2-letter code.
 *
 * Fetches active languages once mounted. Renders nothing until languages are
 * loaded or if the tenant only has one active language (no need to switch).
 *
 * Requires auth since the /language endpoints are portal-authenticated.
 */
export function LanguageSwitcher(): JSX.Element | null {
	const { t, refreshTranslations, currentLanguage } = useTranslation();
	const { token, isAuthenticated } = usePortalAuth();
	const [isOpen, setIsOpen] = useState(false);
	// Seed from the session cache so remounts (the menu drawer unmounts
	// this component on every close) render the flag on first paint
	// instead of popping it in after a refetch (PD-14607).
	const cached = portalLanguageService.getActiveLanguagesCached();
	const [languages, setLanguages] = useState<Array<ActiveLanguage>>(
		cached ?? [],
	);
	const [loaded, setLoaded] = useState(cached != null);
	const popoverRef = useRef<HTMLDivElement>(null);

	// Load active languages once per session (service-level cache). If
	// there's only one active language the component renders null below.
	useEffect(() => {
		if (!isAuthenticated || token == null || loaded) return;
		portalLanguageService
			.getActiveLanguages(token)
			.then((langs) => {
				setLanguages(langs);
				setLoaded(true);
			})
			.catch(() => {
				setLoaded(true);
			});
	}, [isAuthenticated, token, loaded]);

	// Close popover on outside click
	useEffect(() => {
		if (!isOpen) return;
		const handler = (e: MouseEvent) => {
			if (
				popoverRef.current != null &&
				!popoverRef.current.contains(e.target as Node)
			) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [isOpen]);

	const handleChange = useCallback(
		async (code: string) => {
			if (token == null) return;
			const state = usePortalAuthStore.getState();
			state.setLanguagePreference(code);
			state.persist();

			portalLanguageService.updateLanguagePreference(token, code).catch(() => {
				// Best-effort server persistence — localStorage already updated
			});

			await refreshTranslations();
			setIsOpen(false);
		},
		[token, refreshTranslations],
	);

	// Hide when unauthenticated, not loaded yet, or tenant only has one language
	if (!isAuthenticated || !loaded || languages.length <= 1) {
		return null;
	}

	const currentCode = (currentLanguage ?? 'en').toUpperCase();
	const currentLanguageCode = (currentLanguage ?? 'en').toLowerCase();

	return (
		<div className="relative" ref={popoverRef}>
			<button
				type="button"
				onClick={() => setIsOpen((v) => !v)}
				className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm font-medium text-brand-primary-fg hover:bg-brand-primary-fg/10 transition-colors min-h-touch"
				aria-label={t('Change language')}
				aria-expanded={isOpen}
			>
				{/* Show the active language as a country flag instead of a
				    generic globe — at-a-glance which language is set, even
				    on the mobile drawer where the text label is hidden. */}
				<CountryFlag
					code={currentLanguageCode}
					className="h-4 w-6 rounded-sm shadow-[0_0_0_1px_rgba(0,0,0,0.1)]"
					title={currentCode}
				/>
				<span className="hidden md:inline text-xs font-semibold uppercase">
					{t(currentCode)}
				</span>
			</button>

			{isOpen && (
				<div className="absolute right-0 top-full mt-1 z-50 w-48 bg-brand-surface border border-brand-border rounded-lg shadow-lg overflow-hidden">
					<div className="max-h-64 overflow-y-auto py-1">
						{languages.map((lang) => (
							<button
								key={lang.id}
								type="button"
								onClick={() => void handleChange(lang.code)}
								className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-brand-input flex items-center gap-2 min-h-touch"
							>
								<span className="w-4 text-center text-xs">
									{currentLanguage === lang.code && (
										<FontAwesomeIcon
											icon={faCheck}
											className="text-portal-accent"
										/>
									)}
								</span>
								<CountryFlag
									code={lang.code}
									className="h-3.5 w-5 rounded-sm shadow-[0_0_0_1px_rgba(0,0,0,0.1)] shrink-0"
									title={lang.code.toUpperCase()}
								/>
								<span className="uppercase text-xs font-semibold text-brand-muted w-6">
									{t(lang.code.toUpperCase())}
								</span>
								<span className="truncate">{t(lang.name)}</span>
							</button>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
